/**
 * PricingTemplateModal - Create/Edit modal for pricing templates
 *
 * Supports both margin and markup calculation types with auto-save.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Form, Row, Col, InputGroup, Button, Badge, Spinner } from 'react-bootstrap';
import { db } from 'config/firebase';
import { doc, addDoc, updateDoc, collection, collectionGroup, query, where, getDocs, writeBatch, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import { usePricingCalculation } from '../newProducts/hooks/usePricingCalculation';

/**
 * Modal for creating and editing pricing templates.
 *
 * @param {boolean} show - Modal visibility
 * @param {function} onHide - Close callback
 * @param {object|null} template - Template to edit (null for create mode)
 * @param {function} onSave - Callback after save with template data
 */
export const PricingTemplateModal = ({ show, onHide, template, onSave }) => {
  const isEditMode = !!template;

  // Pricing calculation hook for recalculation
  const { calculateBatchPrices } = usePricingCalculation();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    marginType: 'margin',
    prefMargin: '',
    minMargin: '',
    maxMargin: '',
    additionalFulfillmentCost: '',
    roundingEnding: '',
    minimumPrice: '',
  });

  // UI state
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'recalculating' | 'error'
  const [validationErrors, setValidationErrors] = useState({});
  const saveTimeoutRef = useRef(null);
  const [recalculationStats, setRecalculationStats] = useState(null);

  // Initialize form data when template changes
  useEffect(() => {
    if (template) {
      const settings = template.settings || {};
      setFormData({
        name: template.name || '',
        description: template.description || '',
        marginType: settings.marginType || 'margin',
        prefMargin: settings.prefMargin !== undefined ? String(settings.prefMargin * 100) : '',
        minMargin: settings.minMargin !== undefined ? String(settings.minMargin * 100) : '',
        maxMargin: settings.maxMargin !== undefined ? String(settings.maxMargin * 100) : '',
        additionalFulfillmentCost: settings.additionalFulfillmentCost !== undefined ? String(settings.additionalFulfillmentCost) : '',
        roundingEnding: settings.roundingEnding !== undefined && settings.roundingEnding !== null ? String(settings.roundingEnding) : '',
        minimumPrice: settings.minimumPrice !== undefined && settings.minimumPrice !== null ? String(settings.minimumPrice) : '',
      });
    } else {
      // Reset for create mode
      setFormData({
        name: '',
        description: '',
        marginType: 'margin',
        prefMargin: '',
        minMargin: '',
        maxMargin: '',
        additionalFulfillmentCost: '',
        roundingEnding: '',
        minimumPrice: '',
      });
    }
    setValidationErrors({});
    setSaveStatus('idle');
  }, [template, show]);

  // Validate form data
  const validateForm = useCallback((data) => {
    const errors = {};

    // Name is required
    if (!data.name.trim()) {
      errors.name = 'Template name is required';
    }

    // prefMargin validation
    if (data.prefMargin !== '') {
      const prefMarginNum = parseFloat(data.prefMargin);
      if (Number.isNaN(prefMarginNum) || prefMarginNum < 0) {
        errors.prefMargin = 'Must be a positive number';
      } else if (data.marginType === 'margin' && prefMarginNum >= 100) {
        errors.prefMargin = 'Margin must be less than 100%';
      }
    }

    // minMargin validation
    if (data.minMargin !== '') {
      const minMarginNum = parseFloat(data.minMargin);
      if (Number.isNaN(minMarginNum) || minMarginNum < 0) {
        errors.minMargin = 'Must be a positive number';
      } else if (data.marginType === 'margin' && minMarginNum >= 100) {
        errors.minMargin = 'Margin must be less than 100%';
      }
    }

    // maxMargin validation
    if (data.maxMargin !== '') {
      const maxMarginNum = parseFloat(data.maxMargin);
      if (Number.isNaN(maxMarginNum) || maxMarginNum < 0) {
        errors.maxMargin = 'Must be a positive number';
      } else if (data.marginType === 'margin' && maxMarginNum >= 100) {
        errors.maxMargin = 'Margin must be less than 100%';
      }
    }

    // roundingEnding validation (must be 0.XX format)
    if (data.roundingEnding !== '') {
      const roundingPattern = /^0\.\d{2}$/;
      if (!roundingPattern.test(data.roundingEnding)) {
        errors.roundingEnding = 'Must be format 0.XX (e.g., 0.99, 0.49)';
      }
    }

    // minimumPrice validation
    if (data.minimumPrice !== '') {
      const minPriceNum = parseFloat(data.minimumPrice);
      if (Number.isNaN(minPriceNum) || minPriceNum <= 0) {
        errors.minimumPrice = 'Must be a positive number';
      }
    }

    // additionalFulfillmentCost validation
    if (data.additionalFulfillmentCost !== '') {
      const fcNum = parseFloat(data.additionalFulfillmentCost);
      if (Number.isNaN(fcNum) || fcNum < 0) {
        errors.additionalFulfillmentCost = 'Must be a non-negative number';
      }
    }

    return errors;
  }, []);

  /**
   * Recalculate prices for all products using this template.
   * Called after template is saved in edit mode.
   */
  const recalculateProductPrices = useCallback(async (templateId, updatedTemplate) => {
    try {
      // Query all variants using this template
      const q = query(
        collectionGroup(db, 'variants'),
        where('pricingTemplateId', '==', templateId)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log('No products using this template');
        return { updated: 0, errors: 0 };
      }

      // Convert to product array for batch calculation
      const products = snapshot.docs.map(docSnap => ({
        ...docSnap.data(),
        id: docSnap.id,
        ref: docSnap.ref
      }));

      // Calculate new prices using updated template
      const { results, summary } = await calculateBatchPrices(products, updatedTemplate);

      // Batch update products with new prices (Firestore limit is 500 per batch)
      const BATCH_SIZE = 500;
      let totalUpdated = 0;

      for (let i = 0; i < results.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = results.slice(i, i + BATCH_SIZE);

        chunk.forEach(result => {
          const product = products.find(p => p.sku === result.sku);
          if (product?.ref) {
            batch.update(product.ref, {
              calculatedPrice: result.breakdown.price,
              priceBreakdown: result.breakdown,
              priceUpdatedAt: new Date()
            });
            totalUpdated++;
          }
        });

        await batch.commit();
      }

      return { updated: totalUpdated, errors: summary.skipped };
    } catch (error) {
      console.error('Error recalculating prices:', error);
      throw error;
    }
  }, [calculateBatchPrices]);

  // Save template to Firestore
  const saveTemplate = useCallback(async (data) => {
    // Validate first
    const errors = validateForm(data);
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSaveStatus('error');
      return;
    }

    setSaveStatus('saving');

    try {
      const templateData = {
        name: data.name.trim(),
        description: data.description.trim(),
        settings: {
          marginType: data.marginType,
          prefMargin: data.prefMargin !== '' ? parseFloat(data.prefMargin) / 100 : 0,
          minMargin: data.minMargin !== '' ? parseFloat(data.minMargin) / 100 : null,
          maxMargin: data.maxMargin !== '' ? parseFloat(data.maxMargin) / 100 : null,
          additionalFulfillmentCost: data.additionalFulfillmentCost !== '' ? parseFloat(data.additionalFulfillmentCost) : null,
          roundingEnding: data.roundingEnding !== '' ? parseFloat(data.roundingEnding) : null,
          minimumPrice: data.minimumPrice !== '' ? parseFloat(data.minimumPrice) : null,
        },
        type: 'pricing',
        timestamp: {
          updated: serverTimestamp(),
        },
      };

      if (isEditMode) {
        // Update existing template
        await updateDoc(doc(db, 'systemTemplates', template.id), templateData);

        // Trigger recalculation for products using this template
        setSaveStatus('recalculating');
        try {
          const { updated, errors } = await recalculateProductPrices(template.id, templateData);
          setRecalculationStats({ updated, errors });

          if (updated > 0) {
            toast.success(`Template saved. ${updated} product price(s) recalculated.`, { theme: 'colored' });
          } else {
            toast.success('Template saved.', { theme: 'colored' });
          }

          if (errors > 0) {
            toast.warning(`${errors} product(s) could not be recalculated (missing cost data)`, { theme: 'colored' });
          }
        } catch (recalcError) {
          console.error('Recalculation failed:', recalcError);
          toast.error('Template saved but price recalculation failed: ' + recalcError.message, { theme: 'colored' });
        }
      } else {
        // Create new template
        templateData.timestamp.created = serverTimestamp();
        await addDoc(collection(db, 'systemTemplates'), templateData);
      }

      setSaveStatus('saved');

      // Clear saved indicator after 2 seconds
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus('idle');
        setRecalculationStats(null);
      }, 2000);

      // Notify parent
      if (onSave) {
        onSave(templateData);
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template: ' + error.message, { theme: 'colored' });
      setSaveStatus('error');
    }
  }, [isEditMode, template, onSave, validateForm, recalculateProductPrices]);

  // Debounced save (500ms delay)
  const debouncedSave = useCallback(
    debounce((data) => {
      // Only auto-save in edit mode if name is provided
      if (isEditMode && data.name.trim()) {
        saveTemplate(data);
      }
    }, 500),
    [isEditMode, saveTemplate]
  );

  // Handle field change
  const handleChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }

    // Trigger debounced save in edit mode
    if (isEditMode) {
      debouncedSave(newFormData);
    }
  };

  // Handle create (for new templates)
  const handleCreate = async () => {
    await saveTemplate(formData);
    if (Object.keys(validateForm(formData)).length === 0) {
      onHide();
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [debouncedSave]);

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditMode ? 'Edit Pricing Template' : 'Create Pricing Template'}
        </Modal.Title>
        {isEditMode && (
          <div className="ms-auto me-3">
            {saveStatus === 'saving' && (
              <Badge bg="secondary">
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </Badge>
            )}
            {saveStatus === 'recalculating' && (
              <Badge bg="info">
                <Spinner animation="border" size="sm" className="me-1" />
                Recalculating prices...
              </Badge>
            )}
            {saveStatus === 'saved' && (
              <Badge bg="success">
                Saved{recalculationStats?.updated > 0 ? ` (${recalculationStats.updated} prices updated)` : ''}
              </Badge>
            )}
            {saveStatus === 'error' && (
              <Badge bg="danger">Fix errors</Badge>
            )}
          </div>
        )}
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Name */}
          <Form.Group className="mb-3" controlId="templateName">
            <Form.Label>Template Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Standard 30% Margin"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              isInvalid={!!validationErrors.name}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.name}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Description */}
          <Form.Group className="mb-3" controlId="templateDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="e.g., Use for standard products with normal margins"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
            />
          </Form.Group>

          {/* Margin Type */}
          <Form.Group className="mb-3" controlId="marginType">
            <Form.Label>Calculation Type</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                id="margin-type-margin"
                name="marginType"
                label="Margin"
                checked={formData.marginType === 'margin'}
                onChange={() => handleChange('marginType', 'margin')}
              />
              <Form.Check
                inline
                type="radio"
                id="margin-type-markup"
                name="marginType"
                label="Markup"
                checked={formData.marginType === 'markup'}
                onChange={() => handleChange('marginType', 'markup')}
              />
            </div>
            <Form.Text className="text-muted">
              {formData.marginType === 'margin'
                ? 'Margin formula: Price = Cost / (1 - Margin%)'
                : 'Markup formula: Price = Cost * (1 + Markup%)'}
            </Form.Text>
          </Form.Group>

          <Row>
            {/* Target Percentage */}
            <Col md={4}>
              <Form.Group className="mb-3" controlId="prefMargin">
                <Form.Label>Target {formData.marginType === 'margin' ? 'Margin' : 'Markup'} %</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 30"
                    value={formData.prefMargin}
                    onChange={(e) => handleChange('prefMargin', e.target.value)}
                    isInvalid={!!validationErrors.prefMargin}
                    min="0"
                    max={formData.marginType === 'margin' ? '99' : undefined}
                    step="0.1"
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.prefMargin}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>

            {/* Min Percentage */}
            <Col md={4}>
              <Form.Group className="mb-3" controlId="minMargin">
                <Form.Label>Min % (optional)</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 20"
                    value={formData.minMargin}
                    onChange={(e) => handleChange('minMargin', e.target.value)}
                    isInvalid={!!validationErrors.minMargin}
                    min="0"
                    step="0.1"
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.minMargin}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>

            {/* Max Percentage */}
            <Col md={4}>
              <Form.Group className="mb-3" controlId="maxMargin">
                <Form.Label>Max % (optional)</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 50"
                    value={formData.maxMargin}
                    onChange={(e) => handleChange('maxMargin', e.target.value)}
                    isInvalid={!!validationErrors.maxMargin}
                    min="0"
                    step="0.1"
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.maxMargin}
                  </Form.Control.Feedback>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>

          <hr />

          <Row>
            {/* Fulfillment Override */}
            <Col md={4}>
              <Form.Group className="mb-3" controlId="additionalFulfillmentCost">
                <Form.Label>Fulfillment Override</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 3.50"
                    value={formData.additionalFulfillmentCost}
                    onChange={(e) => handleChange('additionalFulfillmentCost', e.target.value)}
                    isInvalid={!!validationErrors.additionalFulfillmentCost}
                    min="0"
                    step="0.01"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.additionalFulfillmentCost}
                  </Form.Control.Feedback>
                </InputGroup>
                <Form.Text className="text-muted">
                  Fallback if FC Optimizer data unavailable
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Rounding Ending */}
            <Col md={4}>
              <Form.Group className="mb-3" controlId="roundingEnding">
                <Form.Label>Rounding Ending</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 0.99"
                  value={formData.roundingEnding}
                  onChange={(e) => handleChange('roundingEnding', e.target.value)}
                  isInvalid={!!validationErrors.roundingEnding}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.roundingEnding}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Apply ending like .99, .49
                </Form.Text>
              </Form.Group>
            </Col>

            {/* Minimum Floor */}
            <Col md={4}>
              <Form.Group className="mb-3" controlId="minimumPrice">
                <Form.Label>Minimum Price Floor</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 9.99"
                    value={formData.minimumPrice}
                    onChange={(e) => handleChange('minimumPrice', e.target.value)}
                    isInvalid={!!validationErrors.minimumPrice}
                    min="0"
                    step="0.01"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.minimumPrice}
                  </Form.Control.Feedback>
                </InputGroup>
                <Form.Text className="text-muted">
                  Never sell below this price
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {isEditMode ? 'Close' : 'Cancel'}
        </Button>
        {!isEditMode && (
          <Button variant="primary" onClick={handleCreate}>
            Create Template
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PricingTemplateModal;
